import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DEMO_FX_RATES, DEMO_USER } from "../../demo/demoData";
import {
  disableDemoMode,
  enableDemoMode,
  onLimitReached,
  resetDemoState,
} from "../../demo/demoStore";
import {
  login as authLogin,
  logout as authLogout,
  loginWithGoogle,
} from "../../services/authService";
import { getFXRates } from "../../services/portfolioService";
import { getProfile } from "../../services/profileService";

export const AuthContext = createContext();

const SUPPORTED_CURRENCIES = ["DEFAULT", "RON", "USD", "EUR", "GBP", "CHF"];
const DEFAULT_CURRENCY = "DEFAULT";
const STORAGE_KEY = "organizeflow_display_currency";

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(undefined);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [userCurrency, setUserCurrency] = useState("USD");
  const auth = getAuth();
  const [rates, setRates] = useState({});
  const [ratesReady, setRatesReady] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoLimitType, setDemoLimitType] = useState(null);
  const [selectedCurrency, setSelectedCurrencyState] = useState(() => {
    // Persist user preference across sessions
    try {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_CURRENCY;
    } catch {
      return DEFAULT_CURRENCY;
    }
  });

  // Fetch user profile including currency
  const fetchUserProfile = async () => {
    try {
      const profileData = await getProfile();
      if (profileData && profileData.length > 0) {
        const userProfile = profileData[0];
        setUserCurrency(userProfile.preferred_currency || "USD");
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserCurrency("USD"); // Fallback to USD
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // If demo mode was active before a page refresh, restore it
      if (sessionStorage.getItem("demoMode") === "true") {
        enableDemoMode();
        setIsDemoMode(true);
        setUser(DEMO_USER);
        setIsLoggedIn(true);
        setEmailVerified(true);
        setUserCurrency("USD");
        setRates(DEMO_FX_RATES);
        setRatesReady(true);
        setLoading(false);
        return;
      }
      if (user) {
        //fetchRates();
        setUser(user);
        setEmailVerified(user.emailVerified);
        const isGoogleUser = user.providerData.some(
          (provider) => provider.providerId === "google.com",
        );
        const isFullyAuthenticated = user.emailVerified || isGoogleUser;

        setIsLoggedIn(isFullyAuthenticated);

        if (isFullyAuthenticated) {
          // Fetch user profile when authenticated
          await fetchUserProfile();
        } else {
          clearContextData();
        }
      } else {
        setIsLoggedIn(false);
        setUser(null);
        setEmailVerified(false);
        setUserCurrency("USD");
        clearContextData();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  const fetchRates = async () => {
    try {
      const response = await getFXRates();
      if (response && response.rates) {
        setRates(response.rates);
        setRatesReady(true);
      }
    } catch (error) {
      console.error("[CurrencyContext] Failed to fetch FX rates:", error);
      // App still works — values just stay in their native currency
      setRatesReady(true);
    }
  };

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selectedCurrency);
  }, [selectedCurrency]);

  useEffect(() => {
    if (isLoggedIn === true) {
      fetchRates();
    }
  }, [isLoggedIn]);

  const clearContextData = () => {
    localStorage.removeItem("selectedContext");
    localStorage.removeItem("selectedGroupId");
    localStorage.removeItem("selectedGroupName");
    localStorage.removeItem("token");
    localStorage.removeItem("selectedCategory");
    localStorage.removeItem("selectedMonths");
    localStorage.removeItem(STORAGE_KEY);
  };

  const login = async (credentials) => {
    return authLogin(credentials).then(async (user) => {
      const isGoogleUser = user.providerData.some(
        (provider) => provider.providerId === "google.com",
      );
      const isFullyAuthenticated = user.emailVerified || isGoogleUser;
      setIsLoggedIn(isFullyAuthenticated);

      if (isFullyAuthenticated) {
        await fetchUserProfile();
      }
    });
  };

  const loginGoogle = async () => {
    return loginWithGoogle().then(async (user) => {
      setIsLoggedIn(true);
      await fetchUserProfile();
    });
  };

  const logout = () => {
    return authLogout().then(() => {
      setIsLoggedIn(false);
      setUser(null);
      setEmailVerified(false);
      setUserCurrency("USD");
      clearContextData();
    });
  };

  const refreshAuthState = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      const updatedUser = auth.currentUser;
      setUser(updatedUser);
      setEmailVerified(updatedUser.emailVerified);

      const isGoogleUser = updatedUser.providerData.some(
        (provider) => provider.providerId === "google.com",
      );
      const isFullyAuthenticated = updatedUser.emailVerified || isGoogleUser;

      setIsLoggedIn(isFullyAuthenticated);

      if (isFullyAuthenticated) {
        await fetchUserProfile();
      }
    }
  };

  // Function to refresh user currency (call this after profile update)
  const refreshUserCurrency = async () => {
    await fetchUserProfile();
  };

  const enterDemoMode = () => {
    sessionStorage.setItem("demoMode", "true");
    localStorage.removeItem("filteredCategoryIds");
    enableDemoMode();
    setIsDemoMode(true);
    setUser(DEMO_USER);
    setIsLoggedIn(true);
    setEmailVerified(true);
    setUserCurrency("USD");
    setRates(DEMO_FX_RATES);
    setRatesReady(true);
    setLoading(false);
  };

  const exitDemoMode = () => {
    sessionStorage.removeItem("demoMode");
    localStorage.removeItem("demoTourShown");
    localStorage.removeItem("demoAddTransactionTourShown");
    localStorage.removeItem("demoProfileTourShown");
    localStorage.removeItem("demoStocksTourShown");
    disableDemoMode();
    resetDemoState();
    setIsDemoMode(false);
    setUser(null);
    setIsLoggedIn(false);
    setEmailVerified(false);
    setUserCurrency("USD");
    setRates({});
    setRatesReady(false);
    clearContextData();
  };

  // Subscribe to demo limit events so the dialog can appear from anywhere
  useEffect(() => {
    if (!isDemoMode) return;
    const unsubscribe = onLimitReached((type) => setDemoLimitType(type));
    return unsubscribe;
  }, [isDemoMode]);

  const setSelectedCurrency = useCallback((currency) => {
    setSelectedCurrencyState(currency);
    try {
      localStorage.setItem(STORAGE_KEY, currency);
    } catch {}
  }, []);

  // Core conversion: amount in fromCurrency → amount in toCurrency
  // All rates are stored as USD→X, so we convert via USD as the pivot:
  //   fromCurrency → USD → toCurrency
  //   result = amount × (rates[toCurrency] / rates[fromCurrency])
  const convert = useCallback(
    (amount, fromCurrency, toCurrency) => {
      amount = parseFloat(amount);
      if (!amount || !fromCurrency || !toCurrency) return amount;
      if (fromCurrency === toCurrency) return amount;
      if (!ratesReady || Object.keys(rates).length === 0) return amount;

      const fromRate = rates[fromCurrency];
      const toRate = rates[toCurrency];

      if (!fromRate || !toRate) {
        console.warn(
          `[AuthContext] Missing rate for ${fromCurrency} or ${toCurrency}`,
        );
        return amount;
      }

      // Convert: amount in fromCurrency → USD → toCurrency
      const amountInUSD = amount / fromRate;
      const convertedAmount = amountInUSD * toRate;

      return convertedAmount;
    },
    [rates, ratesReady],
  );

  // Convenience: convert + format with currency symbol
  const formatMoney = useCallback(
    (amount, fromCurrency, toCurrency = selectedCurrency) => {
      amount = parseFloat(amount);
      if (!amount && amount !== 0) {
        return "0.00";
      }

      // If toCurrency is not provided, use selectedCurrency
      if (toCurrency === "DEFAULT") {
        toCurrency = userCurrency;
      }

      const targetCurrency = toCurrency;

      // Convert the amount
      const convertedAmount = convert(amount, fromCurrency, targetCurrency);

      // Format with currency symbol
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: targetCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(convertedAmount);
    },
    [convert, selectedCurrency],
  );

  const convertAndFormat = (amount, fromCurrency) => {
    if (selectedCurrency === "DEFAULT") {
      return formatMoney(amount, fromCurrency, fromCurrency);
    }
    return formatMoney(
      convert(amount, fromCurrency, selectedCurrency),
      selectedCurrency,
      selectedCurrency,
    );
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        loading,
        userCurrency,
        login,
        loginGoogle,
        logout,
        refreshAuthState,
        refreshUserCurrency,
        selectedCurrency,
        setSelectedCurrency,
        rates,
        ratesReady,
        convert,
        formatMoney,
        supportedCurrencies: SUPPORTED_CURRENCIES,
        convertAndFormat,
        isDemoMode,
        enterDemoMode,
        exitDemoMode,
        demoLimitType,
        clearDemoLimit: () => setDemoLimitType(null),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useCurrency() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useCurrency must be used within a CurrencyProvider");
  }
  return context;
}
